import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router';
import type { PublicBranding } from '@xiltrion/entidad';
import { Alert, AlertDescription } from '@xiltrion/ui/alert';
import { Button } from '@xiltrion/ui/button';
import { Icono } from '@xiltrion/ui/icono';
import { Kbd } from '@xiltrion/ui/kbd';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@xiltrion/ui/sheet';
import { EstadoCargando, EstadoError } from '@xiltrion/ui/bloques/estados';
import { LogoDatalog } from '@xiltrion/ui/marca/logo-datalog';
import { varEtiqueta } from '@xiltrion/ui/paleta';
import { cn } from '@xiltrion/ui/utils';
import { apiGet } from '@/api/client';
import type { PublicApp } from '@/api/types';
import { appColor } from '@/app/app-color';
import { useEntityHint } from '@/app/entity-hint';
import { LogoApp } from '@/app/LogoApp';
import { rememberedEntity } from '@/app/session';
import { FormularioIngreso, type MarcoIngreso } from './FormularioIngreso';
import { MarcoSuite, TituloMarco } from './MarcoSuite';
import { useSigueSiOtraPestanaIngreso } from './sigue-otra-pestana';

/** El degradado de una franja: del tono claro al oscuro del color de la app. */
const degradado = (color: string) => `linear-gradient(165deg, color-mix(in oklch, ${color}, white 42%) -40%, ${color} 45%, color-mix(in oklch, ${color}, black 24%) 120%)`;

/** Con qué volvió una app (GUIA §3.1, `?error=`): qué decirle a la persona. */
function mensajeError(code: string, app: string | null, entidad: string | null): string {
  switch (code) {
    case 'access_denied': return 'Cancelaste el ingreso. Vuelve a intentarlo cuando quieras.';
    case 'login_required': return 'Hace falta ingresar de nuevo.';
    case 'handoff': return 'El ingreso venció a medio camino. Vuelve a ingresar.';
    case 'expired': return `El ingreso${app ? ` a ${app}` : ''} venció antes de terminar. Vuelve a ingresar.`;
    case 'TENANT_NOT_FOUND': return `${entidad ?? 'Tu entidad'} aún no está habilitada en ${app ?? 'esa app'}. Pide a tu administrador que la habilite en la suite.`;
    default: return `No se pudo entrar a ${app ?? 'la app'} (${code}). Intenta de nuevo o pide ayuda a tu administrador.`;
  }
}

/**
 * Portada: la única pantalla de ingreso de la suite y de sus apps (GUIA §3.1).
 *
 * Con entidad conocida (`useEntityHint`: el dominio propio, `?entidad=<slug>` o el `state` del cierre de sesión): una banda inclinada por cada app
 * habilitada (`GET /public/entities/:slug/apps`), con su logo oficial, nombre y descripción. Al pasar el ratón (o
 * enfocar) una app se expande y queda elegida; cuando terminó aparece «Ingresar ↵» dentro de la banda (Enter también
 * sirve) y abre el ingreso en una cortina. Al terminar, el Inicio lleva a esa app (`/?app=<code>`) o, si una app pidió
 * el ingreso por OIDC (`volver=/interaccion/…&app=<code>`), vuelve a la interacción con la cortina de esa app abierta
 * desde el principio.
 *
 * Sin entidad (nadie llega así en la vida real salvo un superadministrador por el dominio propio de Datalog): el
 * catálogo completo a la izquierda, con los logos, y el formulario a la derecha.
 *
 * `?error=<código>` es con lo que una app devolvió (canceló, entidad no aprovisionada…) o `handoff` (el salto al host
 * canónico tras ingresar en un dominio propio venció). En móvil las bandas se apilan sin inclinación.
 */
export function Portada() {
  const [params] = useSearchParams();
  const { slug, listo } = useEntityHint(params);
  const pedida = params.get('app');
  const volver = params.get('volver');
  const error = params.get('error');
  useSigueSiOtraPestanaIngreso(volver);
  const deEntidad = useQuery({ queryKey: ['public-entity-apps', slug], queryFn: () => apiGet<PublicApp[]>(`/public/entities/${slug}/apps`), enabled: !!slug, staleTime: 5 * 60_000, retry: false });
  const marca = useQuery({ queryKey: ['public-branding', slug], queryFn: () => apiGet<PublicBranding>(`/public/entities/${slug}/branding`), enabled: !!slug, staleTime: 5 * 60_000, retry: false });
  // Sin entidad conocida (o si la pedida no existe), el catálogo completo de la suite.
  const sinEntidad = listo && (!slug || deEntidad.isError);
  const catalogo = useQuery({ queryKey: ['public-apps'], queryFn: () => apiGet<PublicApp[]>('/public/apps'), enabled: sinEntidad, staleTime: 5 * 60_000, retry: false });
  // La entidad de la URL queda como entidad activa para después de ingresar (el Inicio salta a la app en ella).
  useEffect(() => { if (slug && !deEntidad.isError) rememberedEntity.set(slug); }, [slug, deEntidad.isError]);
  const apps = sinEntidad ? catalogo.data : deEntidad.data;
  const falla = sinEntidad ? catalogo.error : null;
  const entidad = !sinEntidad && marca.data ? marca.data : null;
  const [elegida, setElegida] = useState<string | null>(pedida);
  const [abierta, setAbierta] = useState(!!pedida);
  const app = apps?.find((a) => a.code === elegida) ?? null;
  // Si la app pedida no está en la lista (no habilitada para la entidad), la cortina no se abre sola.
  useEffect(() => { if (apps && pedida && !apps.some((a) => a.code === pedida)) { setElegida(null); setAbierta(false); } }, [apps, pedida]);
  // Enter desde cualquier parte, como el botón: abre el ingreso de la app elegida (si la cortina está cerrada).
  useEffect(() => {
    if (!elegida || abierta) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Enter' && !(e.target instanceof HTMLElement && e.target.closest('input, textarea, [role=dialog]'))) setAbierta(true); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [elegida, abierta]);
  if (falla) return <div className="grid min-h-dvh place-items-center p-6"><EstadoError titulo="No se pudo cargar la suite." /></div>;
  if (!apps) return <div className="grid min-h-dvh place-items-center"><EstadoCargando /></div>;
  if (!apps.length) return <div className="grid min-h-dvh place-items-center p-6"><EstadoError titulo={entidad ? `${entidad.name} no tiene apps habilitadas.` : 'La suite no tiene apps activas.'} /></div>;

  const aviso = error ? <Alert variant={error === 'access_denied' || error === 'expired' ? 'warning' : 'destructive'}><Icono nombre="mdi:alert-circle-outline" /><AlertDescription>{mensajeError(error, app?.name ?? null, entidad?.name ?? null)}</AlertDescription></Alert> : undefined;
  const destino = (a: PublicApp) => volver ?? `/?app=${encodeURIComponent(a.code)}`;

  if (sinEntidad) return <PortadaSuite apps={apps} app={app} elegir={setElegida} volver={destino} aviso={aviso} />;

  const elegir = (code: string) => { setElegida(code); setAbierta(true); };
  return (
    <main className="relative flex min-h-dvh flex-col">
      {/* cabecera sobre las bandas: clara a la izquierda (la entidad) y se funde a transparente hacia la derecha (la marca de Datalog sobre el color) */}
      <header className="pointer-events-none absolute inset-x-0 top-0 z-10 flex h-16 items-center justify-between px-6 animate-in fade-in duration-500 fill-mode-both lg:px-8" style={{ background: 'linear-gradient(90deg, var(--background) 0%, var(--background) 40%, transparent 100%)' }}>
        <div className="flex items-center gap-3">
          {entidad?.logoUrl && <img src={entidad.logoUrl} alt="" className="h-10 w-auto max-w-44 object-contain dark:rounded-md dark:bg-white dark:px-1.5 dark:py-1" />}
          {entidad && <span className="text-sm font-semibold text-foreground">{entidad.name}</span>}
        </div>
        <LogoDatalog className="h-[26px] w-auto drop-shadow-md" />
      </header>
      {/* El fondo lleva el degradado de la primera app a pantalla completa y la primera franja es transparente sobre él:
          mientras las demás entran desde la derecha, lo que se ve es la misma superficie, sin costura. */}
      <div className="relative flex flex-1 flex-col overflow-hidden [--inc:0px] lg:flex-row lg:[--inc:90px]" style={{ background: degradado(varEtiqueta(appColor(apps[0]!))) }}>
        {apps.map((a, i) => {
          const color = varEtiqueta(appColor(a));
          const primera = i === 0;
          const activa = elegida === a.code;
          return (
            <section
              key={a.code}
              role="button"
              tabIndex={0}
              aria-pressed={activa}
              aria-label={`${a.name}: ${a.description}`}
              onMouseEnter={() => setElegida(a.code)}
              onFocus={() => setElegida(a.code)}
              onClick={() => (activa ? setAbierta(true) : setElegida(a.code))}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); elegir(a.code); } }}
              className={cn(
                'relative flex min-h-[30vh] flex-1 cursor-pointer flex-col items-center justify-center gap-3 overflow-hidden px-8 py-8 text-primary-foreground outline-none transition-[flex-grow] duration-700 ease-out lg:min-h-0 lg:gap-4 lg:py-12',
                // las demás entran deslizando desde la derecha, opacas (con fundido se vería el solape), de derecha a izquierda
                !primera && 'animate-in slide-in-from-right duration-700 ease-out fill-mode-both',
                !primera && i < apps.length - 1 && 'lg:px-[calc(2rem+var(--inc)/2)]',
              )}
              style={{
                flexGrow: elegida ? (activa ? 1.7 : 0.8) : 1,
                // Borde inclinado: cada banda se solapa `--inc` con la anterior y recorta en diagonal solo su lado izquierdo; el
                // derecho lo tapa la siguiente (dos recortes coincidentes dejarían asomar el fondo en un pelo de antialias).
                clipPath: primera ? undefined : 'polygon(var(--inc) 0, 100% 0, 100% 100%, 0 100%)',
                marginLeft: primera ? undefined : 'calc(var(--inc) * -1)',
                // la primera es transparente: su superficie es el degradado del fondo, sin costura con él mientras las demás entran
                background: primera ? undefined : degradado(color),
                animationDelay: `${(apps.length - 1 - i) * 120}ms`,
              }}
            >
              {/* retícula tenue y brillo arriba, como el panel de la pantalla de acceso */}
              <div aria-hidden className="pointer-events-none absolute inset-0 opacity-[0.07]" style={{ backgroundImage: 'linear-gradient(currentColor 1px, transparent 1px), linear-gradient(90deg, currentColor 1px, transparent 1px)', backgroundSize: '44px 44px', maskImage: 'radial-gradient(circle at 50% 40%, black, transparent 70%)' }} />
              <div aria-hidden className="pointer-events-none absolute inset-0" style={{ background: 'radial-gradient(circle at 50% 35%, color-mix(in srgb, currentColor 22%, transparent), transparent 40%)' }} />
              <LogoApp app={a} className={cn('portada-flota relative w-20 transition-transform duration-700 ease-out lg:w-36', activa && 'scale-110')} />
              <h2 className="relative text-2xl font-semibold tracking-tight lg:text-4xl">{a.name}</h2>
              <p className="relative min-h-10 max-w-[26ch] text-center text-sm leading-snug opacity-85 lg:text-[15px]">{a.description}</p>
              {/* el botón aparece cuando la franja terminó de expandirse (la transición dura 700 ms) */}
              <div className="relative mt-2 flex h-12 justify-center lg:mt-4">
                {activa && (
                  <Button size="lg" variant="outline" aria-label={`Ingresar a ${a.name}`} className="h-12 rounded-full border-0 bg-background px-8 text-base text-foreground shadow-xl ring-[6px] ring-background/20 animate-in fade-in slide-in-from-bottom-2 duration-500 delay-700 fill-mode-both hover:-translate-y-0.5 hover:bg-background" onClick={(e) => { e.stopPropagation(); setAbierta(true); }}>
                    Ingresar<Kbd className="ml-1">↵</Kbd>
                  </Button>
                )}
              </div>
            </section>
          );
        })}
      </div>

      <Sheet open={abierta} onOpenChange={setAbierta}>
        {/* el formulario toma el color de la app (`.portada-app` de `index.css`): botón, enlaces y anillo de foco */}
        <SheetContent side="bottom" className="portada-app min-h-[70vh] items-center justify-center p-6" style={app ? ({ '--portada-app': varEtiqueta(appColor(app)) } as CSSProperties) : undefined}>
          <div className="w-full max-w-sm">
            {app && (
              <FormularioIngreso
                volver={destino(app)}
                titulo={`Ingresar a ${app.name}`}
                aviso={aviso}
                marco={({ titulo, subtitulo, children }) => (
                  <>
                    <LogoApp app={app} className="mb-5 w-16" />
                    <SheetHeader className="p-0 pb-5"><SheetTitle className="text-2xl">{titulo}</SheetTitle>{subtitulo && <SheetDescription>{subtitulo}</SheetDescription>}</SheetHeader>
                    {children}
                  </>
                )}
              />
            )}
          </div>
        </SheetContent>
      </Sheet>
    </main>
  );
}

/**
 * Portada sin entidad: la suite entera, en negro (`MarcoSuite`). A la izquierda los logos de las apps (clic en uno para
 * entrar directo a ella); a la derecha el formulario. Es la entrada del superadministrador por el dominio propio de
 * Datalog; una persona de una entidad entra sola a la suya y el Inicio la lleva a su app.
 */
function PortadaSuite({ apps, app, elegir, volver, aviso }: { apps: PublicApp[]; app: PublicApp | null; elegir: (code: string | null) => void; volver: (a: PublicApp) => string; aviso?: ReactNode }) {
  const marco: MarcoIngreso = ({ titulo, subtitulo, children }) => (
    <>
      <TituloMarco app={app} titulo={titulo} subtitulo={subtitulo} />
      {children}
    </>
  );
  return (
    <MarcoSuite apps={apps} app={app} elegir={elegir} superadmin>
      <FormularioIngreso
        key={app?.code ?? 'suite'}
        volver={app ? volver(app) : '/'}
        titulo={app ? `Ingresar a ${app.name}` : 'Ingresa a la suite'}
        aviso={aviso}
        marco={marco}
      />
    </MarcoSuite>
  );
}
